import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { Avatar, AvatarFallback, AvatarImage } from "@tellescope/ui";
import { formatInitials, formatName } from "@tellescope/utils";
import { toast } from "sonner";
import { useOpenSMSThread } from "../hooks";

export interface SMSNotificationProps {
  message: string;
  sms: TellescopeModelFromName<"sms_messages">;
  enduser?: TellescopeModelFromName<"endusers">;
  toastId?: string | number;
}

export const SMSNotification = ({ message, sms, enduser, toastId }: SMSNotificationProps) => {
  const openSMSThread = useOpenSMSThread();

  const handleClick = async () => {
    await openSMSThread(sms);
    if (toastId !== undefined) {
      toast.dismiss(toastId);
    }
  };

  return (
    <div
      onClick={handleClick}
      className="flex gap-3 items-center cursor-pointer hover:bg-accent p-4 rounded-lg bg-background border border-border shadow-lg transition-colors min-w-[320px]"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <Avatar className="h-10 w-10">
        {enduser ? (
          <>
            <AvatarImage src={enduser.avatar} alt={formatName(enduser)} />
            <AvatarFallback useStaticBackground={true} id={enduser.id}>
              {formatInitials(enduser)}
            </AvatarFallback>
          </>
        ) : (
          <AvatarFallback>?</AvatarFallback>
        )}
      </Avatar>

      <div className="flex flex-col gap-1 flex-1 min-w-0">
        <p className="font-semibold text-sm truncate">{enduser ? formatName(enduser) : "Unknown Contact"}</p>
        <p className="text-sm text-muted-foreground line-clamp-2">{message}</p>
      </div>
    </div>
  );
};
